import React, { useState, useEffect } from 'react';
import './VocabGame2.css';

function VocabGame2({ 
  wordPairs, 
  onComplete, 
  onWordAttempt, 
  onWordOrderGenerated,
  normalizeHebrew 
}) {
  const [shuffledWordPairs, setShuffledWordPairs] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);
  const [options, setOptions] = useState([]);
  const [showFireworks, setShowFireworks] = useState(false);
  const [showFixPrompt, setShowFixPrompt] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [wordAttempts, setWordAttempts] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSelectLocked, setIsSelectLocked] = useState(false);

  // Create a single AudioContext for all sounds (mobile optimization)
  const audioContextRef = React.useRef(null);
  const selectLockedRef = React.useRef(false);
  const correctPositionPlanRef = React.useRef([]);
  
  // Initialize/resume AudioContext on the user's click/tap before playing sounds
  const initAudioContext = async () => {
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return null;

      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new AudioContextClass();
      }

      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      return audioContextRef.current.state === 'running' ? audioContextRef.current : null;
    } catch (error) {
      console.warn('AudioContext could not be initialized:', error);
      return null;
    }
  };
  
  React.useEffect(() => {
    // Clean up on unmount
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Fisher-Yates shuffle algorithm for better randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const buildBalancedCorrectPositionPlan = (wordCount) => {
    const positionCount = Math.min(4, wordCount);
    const positions = Array.from({ length: positionCount }, (_, index) => index + 1);
    const counts = Object.fromEntries(positions.map(position => [position, Math.floor(wordCount / positionCount)]));
    const remainder = wordCount % positionCount;
    const extraPositions = shuffleArray(positions.filter(position => position !== 1));

    if (positions.includes(1)) {
      extraPositions.push(1);
    }

    for (let i = 0; i < remainder; i++) {
      counts[extraPositions[i]] += 1;
    }

    const plan = positions.flatMap(position => Array(counts[position]).fill(position));
    return shuffleArray(plan);
  };

  const lockSelect = () => {
    selectLockedRef.current = true;
    setIsSelectLocked(true);
  };

  const unlockSelect = () => {
    selectLockedRef.current = false;
    setIsSelectLocked(false);
  };

  // Initialize game once with shuffled words
  useEffect(() => {
    if (wordPairs.length > 0 && !isInitialized) {
      const indexedWordPairs = wordPairs.map((pair, originalIndex) => ({
        ...pair,
        originalIndex
      }));
      const shuffled = shuffleArray(indexedWordPairs);
      correctPositionPlanRef.current = buildBalancedCorrectPositionPlan(shuffled.length);
      setShuffledWordPairs(shuffled);
      if (onWordOrderGenerated) {
        onWordOrderGenerated(shuffled.map(pair => pair.originalIndex));
      }
      generateOptionsForWord(0, shuffled);
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordPairs, isInitialized]);

  const generateOptionsForWord = (wordIndex, pairsArray) => {
    const currentPair = pairsArray[wordIndex];
    const correctAnswer = currentPair.hebrew;
    
    const otherPairs = pairsArray.filter((_, idx) => idx !== wordIndex);
    const shuffledOthers = shuffleArray(otherPairs);
    const usedAnswers = new Set([normalizeHebrew(correctAnswer)]);
    const wrongAnswers = [];

    for (const pair of shuffledOthers) {
      const normalizedAnswer = normalizeHebrew(pair.hebrew);
      if (!normalizedAnswer || usedAnswers.has(normalizedAnswer)) continue;

      usedAnswers.add(normalizedAnswer);
      wrongAnswers.push(pair.hebrew);
      if (wrongAnswers.length === 3) break;
    }
    
    const allOptions = [correctAnswer, ...wrongAnswers];
    const shuffledOptions = shuffleArray(allOptions);
    const correctIndex = shuffledOptions.findIndex(opt => normalizeHebrew(opt) === normalizeHebrew(correctAnswer));
    const plannedPosition = correctPositionPlanRef.current[wordIndex] || 1;
    const clampedPosition = Math.min(plannedPosition, shuffledOptions.length);
    const startIndex = (correctIndex - (clampedPosition - 1) + shuffledOptions.length) % shuffledOptions.length;
    
    setOptions(shuffledOptions);
    setCurrentOptionIndex(startIndex);
  };

  const handlePreviousOption = () => {
    setCurrentOptionIndex((prev) => (prev - 1 + options.length) % options.length);
  };

  const handleNextOption = () => {
    setCurrentOptionIndex((prev) => (prev + 1) % options.length);
  };

  // Sound effects using Web Audio API (optimized for mobile)
  const playSound = (audioContext, frequency, duration, type = 'success', startTime = audioContext.currentTime) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'success') {
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
    } else {
      oscillator.frequency.value = frequency;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.2, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
    }
    
    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
  };

  const playSuccessSound = async () => {
    const audioContext = await initAudioContext();
    if (!audioContext) return;

    const startTime = audioContext.currentTime;
    playSound(audioContext, 523.25, 0.15, 'success', startTime); // C5
    playSound(audioContext, 659.25, 0.15, 'success', startTime + 0.1); // E5
    playSound(audioContext, 783.99, 0.2, 'success', startTime + 0.2); // G5
  };

  const playErrorSound = async () => {
    const audioContext = await initAudioContext();
    if (!audioContext) return;

    playSound(audioContext, 200, 0.3, 'error');
  };

  const handleSelect = () => {
    if (
      shuffledWordPairs.length === 0 ||
      selectLockedRef.current ||
      showFireworks ||
      showFixPrompt ||
      showCorrectAnswer
    ) {
      return;
    }

    lockSelect();
    
    const currentPair = shuffledWordPairs[currentWordIndex];
    const selectedAnswer = options[currentOptionIndex];
    const correctAnswer = currentPair.hebrew;
    
    const isCorrect = normalizeHebrew(selectedAnswer) === normalizeHebrew(correctAnswer);
    
    // Find the original index in wordPairs (before shuffling)
    const originalIndex = currentPair.originalIndex ?? wordPairs.findIndex(
      pair => pair.arabic === currentPair.arabic && pair.hebrew === currentPair.hebrew
    );
    
    const currentAttempts = (wordAttempts[originalIndex] || 0) + 1;
    setWordAttempts(prev => ({
      ...prev,
      [originalIndex]: currentAttempts
    }));
    
    if (isCorrect) {
      playSuccessSound();
      setShowFireworks(true);
      
      if (onWordAttempt) {
        onWordAttempt(originalIndex, true, currentAttempts);
      }
      
      setTimeout(() => {
        setShowFireworks(false);
        if (currentWordIndex < shuffledWordPairs.length - 1) {
          const nextIndex = currentWordIndex + 1;
          setCurrentWordIndex(nextIndex);
          generateOptionsForWord(nextIndex, shuffledWordPairs);
          unlockSelect();
        } else {
          setIsCompleted(true);
          if (onComplete) {
            onComplete();
          }
        }
      }, 1500);
    } else {
      playErrorSound();
      setShowFixPrompt(true);
    }
  };

  const handleFix = () => {
    setShowFixPrompt(false);
    unlockSelect();
  };

  const handleShowCorrect = () => {
    lockSelect();
    const currentPair = shuffledWordPairs[currentWordIndex];
    
    // Find the original index in wordPairs (before shuffling)
    const originalIndex = currentPair.originalIndex ?? wordPairs.findIndex(
      pair => pair.arabic === currentPair.arabic && pair.hebrew === currentPair.hebrew
    );
    
    const currentAttempts = wordAttempts[originalIndex] || 1;
    
    setShowFixPrompt(false);
    setShowCorrectAnswer(true);
    
    if (onWordAttempt) {
      onWordAttempt(originalIndex, false, currentAttempts);
    }
    
    setTimeout(() => {
      setShowCorrectAnswer(false);
      if (currentWordIndex < shuffledWordPairs.length - 1) {
        const nextIndex = currentWordIndex + 1;
        setCurrentWordIndex(nextIndex);
        generateOptionsForWord(nextIndex, shuffledWordPairs);
        unlockSelect();
      } else {
        setIsCompleted(true);
        if (onComplete) {
          onComplete();
        }
      }
    }, 2000);
  };

  if (isCompleted || shuffledWordPairs.length === 0) {
    return null;
  }

  const currentPair = shuffledWordPairs[currentWordIndex];

  return (
    <div className="vocab-game2-container">
      <div className="game2-word-display">
        <p className="game2-arabic-word" lang="ar" dir="rtl">
          {currentPair?.arabic}
        </p>
      </div>

      <div className="game2-card-container">
        <button 
          className="game2-arrow-button game2-arrow-right"
          onClick={handlePreviousOption}
          aria-label="אפשרות קודמת"
          disabled={showCorrectAnswer}
        >
          <div className="nav-button-icon back-icon"></div>
        </button>

        <div className={`game2-option-card ${showCorrectAnswer ? 'correct-answer' : ''}`}>
          <p className="game2-option-text">
            {showCorrectAnswer ? currentPair?.hebrew : options[currentOptionIndex]}
          </p>
        </div>

        <button 
          className="game2-arrow-button game2-arrow-left"
          onClick={handleNextOption}
          aria-label="אפשרות הבאה"
          disabled={showCorrectAnswer}
        >
          <div className="nav-button-icon next-icon"></div>
        </button>
      </div>

      {!showCorrectAnswer && (
        <button 
          className="game2-select-button"
          onClick={handleSelect}
          disabled={isSelectLocked || showFireworks || showFixPrompt}
        >
          בחר
        </button>
      )}

      <div className="game2-progress">
        {currentWordIndex + 1}/{wordPairs.length}
      </div>

      {showFireworks && (
        <div className="fireworks-container">
          <div className="firework firework-1"></div>
          <div className="firework firework-2"></div>
          <div className="firework firework-3"></div>
          <div className="firework firework-4"></div>
        </div>
      )}

      {showFixPrompt && (
        <>
          <div className="game2-overlay"></div>
          <div className="game2-fix-prompt">
            <p>התשובה שגויה</p>
            <div className="game2-fix-buttons">
              <button onClick={handleFix} className="game2-fix-btn">
                כן, אתקן
              </button>
              <button onClick={handleShowCorrect} className="game2-show-btn">
                לא, הצג תשובה נכונה
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default VocabGame2;
